import React from 'react'
import { components, useTranslation } from 'cng-web-lib'
import { Grid } from '@material-ui/core'
import { useFormContext } from 'react-hook-form'
import Namespace from 'src/constants/locale/Namespace'
import UaeMpciKeys from 'src/constants/locale/key/UaeMpci'
import makeValidationSchema from './PartiesInfoMakeValidationSchema'

const {
  form: {
    field: {
      CngTextField,
      CngCodeMasterAutocompleteField
    },
  },
  CngGridItem,
} = components

const DEFAULT_INITIAL_VALUES = Object.freeze({
  id: null,
  billNo: "",
  parentBillNo: "",
  partyType: "",
  partyIdentificationType: "",
  partyId: "",
  establishmentOrIndividual: "",
  partyName: "",
  street: "",
  streetNumber: "",
  postalCode: "",
  city: "",
  country: "",
  contactLevel: "",
  contactName: "",
  telephoneNumber: "",
  emailId: "",
  url: "",
  forwardingNotes: ""
})

const FORMIK_PROPS = {
  initialValues: { ...DEFAULT_INITIAL_VALUES },
  makeValidationSchema: makeValidationSchema
}

function Fields({ disabled }) {
  const { setValue, watch } = useFormContext()
  const partyType = watch('partyType')
  const { translate } = useTranslation(Namespace.UAE_MPCI)
  const t = makeTranslatedTextsObject()

  function makeTranslatedTextsObject() {
    let partyType = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.PARTY_TYPE)
    let partyIdentificationType = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.PARTY_IDENTIFICATION_TYPE)
    let partyId = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.PARTY_ID)
    let establishmentOrIndividual = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.ESTABLISHMENT_OR_INDIVIDUAL)
    let partyName = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.PARTY_NAME)
    let street = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.STREET)
    let streetNumber = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.STREET_NUMBER)
    let postalCode = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.POSTAL_CODE)
    let city = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.CITY)
    let country = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.COUNTRY)
    let contactLevel = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.CONTACT_LEVEL)
    let contactName = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.CONTACT_NAME)
    let telephoneNumber = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.TELEPHONE_NUMBER)
    let emailId = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.EMAIL_ID)
    let url = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.URL)
    let forwardingNotes = translate(Namespace.UAE_MPCI, UaeMpciKeys.PartiesInfo.FORWARDING_NOTES)

    return {
      partyType, partyIdentificationType, partyId, establishmentOrIndividual,
      partyName, street, streetNumber, postalCode, city, country,
      contactLevel, contactName, telephoneNumber, emailId, url, forwardingNotes
    }
  }

  return (
    <Grid container spacing={1}>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngCodeMasterAutocompleteField
          required
          name="partyType"
          label={t.partyType}
          disabled={disabled}
          codeType='UAE_MPCI_PARTY_TYPE'
          size='small'
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngCodeMasterAutocompleteField
          required={partyType === 'CN' || partyType === 'CZ'}
          name="partyIdentificationType"
          label={t.partyIdentificationType}
          disabled={disabled}
          codeType='UAE_MPCI_PARTY_ID_TYPE'
          size='small'
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="partyId"
          label={t.partyId}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 35 }}
          onChange={(e) => setValue("partyId", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngCodeMasterAutocompleteField
          name="establishmentOrIndividual"
          label={t.establishmentOrIndividual}
          disabled={disabled}
          codeType='UAE_MPCI_ESTABLISHMENT_INDIVIDUAL'
          size='small'
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          required
          name="partyName"
          label={t.partyName}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 70 }}
          onChange={(e) => setValue("partyName", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="street"
          label={t.street}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 256 }}
          onChange={(e) => setValue("street", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="streetNumber"
          label={t.streetNumber}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 256 }}
          onChange={(e) => setValue("streetNumber", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="postalCode"
          label={t.postalCode}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 256 }}
          onChange={(e) => setValue("postalCode", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="city"
          label={t.city}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 35 }}
          onChange={(e) => setValue("city", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="country"
          label={t.country}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 2 }}
          onChange={(e) => setValue("country", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngCodeMasterAutocompleteField
          name="contactLevel"
          label={t.contactLevel}
          disabled={disabled}
          codeType='UAE_MPCI_CONTACT_LEVEL'
          size='small'
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="contactName"
          label={t.contactName}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 256 }}
          onChange={(e) => setValue("contactName", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="telephoneNumber"
          label={t.telephoneNumber}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 512 }}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="emailId"
          label={t.emailId}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 512 }}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={6} lg={4}>
        <CngTextField
          name="url"
          label={t.url}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 512 }}
        />
      </CngGridItem>
      <CngGridItem xs={12} sm={12} lg={12}>
        <CngTextField
          name="forwardingNotes"
          label={t.forwardingNotes}
          disabled={disabled}
          size='small'
          inputProps={{ maxLength: 512 }}
          multiline
          rows={2}
          onChange={(e) => setValue("forwardingNotes", e.target.value.toUpperCase(), { shouldValidate: true })}
        />
      </CngGridItem>
    </Grid>
  )
}

const FormProperties = Object.freeze({
  formikProps: FORMIK_PROPS,
  Fields: Fields
})

export default FormProperties
